import React, { useState, useEffect } from 'react';
import ShareCard from '../components/Share/ShareCard';
import { getUserActiveShares, revokeShareKey, cleanupExpiredShares } from '../services/shareService';
import { useAuth } from '../contexts/AuthContext';
import { SearchBox, ActionButton, FilterLayout } from '../components/UI';
import { RefreshCw } from 'lucide-react';
import './ShareManagementPage.css';

/**
 * 공유관리 페이지 컴포넌트
 * 사용자의 활성 공유 목록을 표시하고 관리할 수 있는 기능 제공
 */
const ShareManagementPage = () => {
  const { user } = useAuth();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // 공유 목록 로드
  const loadShares = async () => {
    if (!user) {
      setError('사용자 정보를 불러올 수 없습니다.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // 만료된 공유 정리
      await cleanupExpiredShares();
      const activeShares = await getUserActiveShares(user.uid);
      setShares(activeShares);
    } catch (err) {
      console.error('공유 목록 로드 실패:', err);
      setError('공유 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 공유 취소 핸들러
  const handleRevoke = async (accessToken) => {
    if (!user) {
      alert('사용자 정보를 확인할 수 없습니다.');
      return;
    }

    try {
      await revokeShareKey(accessToken, user.uid);
      
      // 목록에서 제거
      setShares(prevShares => 
        prevShares.filter(share => share.accessToken !== accessToken)
      );
      
      alert('공유가 취소되었습니다.');
    } catch (error) {
      console.error('공유 취소 실패:', error);
      alert('공유 취소에 실패했습니다.');
    }
  };

  // 페이지 로드 시 공유 목록 로드
  useEffect(() => {
    if (user) {
      loadShares();
    }
  }, [user?.uid]);

  // 새로고침 핸들러
  const handleRefresh = () => {
    loadShares();
  };

  // 검색 및 필터링된 공유 목록
  const filteredShares = shares.filter(share => {
    const matchesSearch = share.passwordTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         share.passwordWebsite.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterStatus === 'all') return matchesSearch;
    
    const now = new Date();
    const isExpired = now > share.expiresAt.toDate();
    const isCompleted = share.currentUses >= share.maxUses;
    
    switch (filterStatus) {
      case 'active':
        return matchesSearch && !isExpired && !isCompleted;
      case 'expired':
        return matchesSearch && isExpired;
      case 'completed':
        return matchesSearch && isCompleted;
      default:
        return matchesSearch;
    }
  });

  // 공유 통계 계산
  const getShareStats = () => {
    const now = new Date();
    const active = shares.filter(s => s.expiresAt.toDate() > now && s.currentUses < s.maxUses).length;
    const expired = shares.filter(s => s.expiresAt.toDate() < now).length;
    const completed = shares.filter(s => s.currentUses >= s.maxUses).length;
    
    return { active, expired, completed, total: shares.length };
  };

  const stats = getShareStats();

  if (!user) {
    return (
      <div className="share-management-page">
        <div className="empty-state">
          <div className="empty-icon">🔐</div>
          <h3>로그인이 필요합니다</h3>
          <p>공유 관리 기능을 사용하려면 먼저 로그인해주세요.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="share-management-page">
      {/* 통계 카드 */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">총 공유</div>
          </div>
        </div>
        <div className="stat-card active">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.active}</div>
            <div className="stat-label">활성 공유</div>
          </div>
        </div>
        <div className="stat-card expired">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <div className="stat-value">{stats.expired}</div>
            <div className="stat-label">만료됨</div>
          </div>
        </div>
        <div className="stat-card completed">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-value">{stats.completed}</div>
            <div className="stat-label">완료됨</div>
          </div>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <FilterLayout
        searchSlot={
          <SearchBox
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="패스워드 제목 또는 웹사이트 검색..."
          />
        }
        filterSlot={
          <>
            <div className="filter-buttons">
              <ActionButton
                variant={filterStatus === 'all' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setFilterStatus('all')}
              >
                전체
              </ActionButton>
              <ActionButton
                variant={filterStatus === 'active' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setFilterStatus('active')}
              >
                활성
              </ActionButton>
              <ActionButton
                variant={filterStatus === 'expired' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setFilterStatus('expired')}
              >
                만료
              </ActionButton>
              <ActionButton
                variant={filterStatus === 'completed' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setFilterStatus('completed')}
              >
                완료
              </ActionButton>
            </div>

            <ActionButton
              variant="ghost"
              size="icon-only"
              icon={RefreshCw}
              onClick={handleRefresh}
              disabled={loading}
              title="새로고침"
            />
          </>
        }
      />

      {/* 공유 목록 */}
      <div className="share-list-container">
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>공유 목록을 불러오는 중...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <div className="error-icon">❌</div>
            <h3>오류가 발생했습니다</h3>
            <p>{error}</p>
            <button 
              onClick={handleRefresh} 
              className="btn btn-primary"
            >
              다시 시도
            </button>
          </div>
        )}

        {!loading && !error && filteredShares.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>
              {searchTerm || filterStatus !== 'all' 
                ? '검색 결과가 없습니다' 
                : '활성 공유가 없습니다'
              }
            </h3>
            <p>
              {searchTerm || filterStatus !== 'all'
                ? '다른 검색어나 필터를 사용해보세요.'
                : '패스워드 목록에서 "공유" 버튼을 클릭하여 새 공유를 생성하세요.'
              }
            </p>
          </div>
        )}

        {!loading && !error && filteredShares.length > 0 && (
          <div className="share-list">
            {filteredShares.map((share) => (
              <ShareCard
                key={share.shareKey}
                shareData={share}
                onRevoke={handleRevoke}
                passwordTitle={share.passwordTitle || share.title || '제목 없음'}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareManagementPage;